import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {HeaderFilter} from 'src/components/header-filter';
import {FILTER_WIDTH} from 'src/constants';
import {DicItem} from 'src/modules/user-profile/hooks/use-supported-user-audit-actions';
import {formatTimestamp} from 'src/utils/date-utils';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Highlight, getIndexesOfMatches} from '@pb/kit-component-highlight';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {type PaginationChangeHandler, PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {auditLogPagination} from '../user-profile.module.styl';

import type {AuditEntry} from 'src/openapi/admin/models/AuditEntry';


const MIN_PAGE_SIZE = 25;

const TimestampCell: React.FC<{timestamp: string}> = ({timestamp}) => {
    const value = useMemo(() => formatTimestamp(timestamp), [timestamp]);

    return <EllipsisTextHint title={value}>{value}</EllipsisTextHint>;
};

function formatInitiator(entry: AuditEntry): string {
    if (entry.initiatorUser != null) {
        const {firstName, lastName, email} = entry.initiatorUser;
        const name = [firstName, lastName].filter(Boolean).join(' ').trim();
        return name || email || `User ${entry.initiatorUser.userId}`;
    }
    if (entry.initiatorSystem != null) {
        return entry.initiatorSystemProcess != null
            ? `${entry.initiatorSystem} / ${entry.initiatorSystemProcess}`
            : entry.initiatorSystem;
    }
    return '—';
}

const DescriptionCell: React.FC<{description: string, query: string}> = ({description, query}) => (
    <EllipsisTextHint title={description}>
        {query
            ? <Highlight text={description} matchedChunks={getIndexesOfMatches(description, query)}/>
            : description}
    </EllipsisTextHint>
);

type Props = {
    entries: AuditEntry[],
    totalCount: number,
    loading: boolean,
    page: number,
    pageSize: number,
    searchQuery: string,
    actionType: string | undefined,
    actionOptions: DicItem[],
    getTitleById: (id: string) => string | undefined,
    onPageChange: (page: number) => void,
    onPageSizeChange: (size: number) => void,
    onActionTypeChange: (actionType: string | undefined) => void,
};

export const AuditLogTable: React.FC<Props> = ({
    entries,
    totalCount,
    loading,
    page,
    pageSize,
    searchQuery,
    actionType,
    actionOptions,
    getTitleById,
    onPageChange,
    onPageSizeChange,
    onActionTypeChange,
}) => {
    const [tempActionType, setTempActionType] = useState<string | undefined>(actionType);

    useEffect(() => {
        setTempActionType(actionType);
    }, [actionType]);

    const handlePaginationChange = useCallback<PaginationChangeHandler>(({currentPage}) => {
        onPageChange(currentPage);
    }, [onPageChange]);

    const handlePageSizeChange = useCallback((_currentPage: number, size: number) => {
        onPageSizeChange(Math.max(size, MIN_PAGE_SIZE));
    }, [onPageSizeChange]);

    if (loading) {
        return <Loading type="ICON"/>;
    }

    return (
        <>
            <Table borderedColumns={false}>
                <Head>
                    <Row>
                        <Cell width={50}>#</Cell>
                        <Cell verticalAlign="top" width={250}>
                            <HeaderFilter
                                caption="Action Type"
                                filterActive={actionType != null ? true : undefined}
                                sortable={false}
                                showFilter={actionOptions.length > 1}
                                onReset={() => {
                                    setTempActionType(undefined);
                                    onActionTypeChange(undefined);
                                }}
                                onApply={() => onActionTypeChange(tempActionType)}
                                onCancel={() => setTempActionType(actionType)}
                                contentWidth={FILTER_WIDTH}>
                                <div>
                                    <Label>Action Type</Label>
                                    <ComboBox<string, string>
                                        value={tempActionType}
                                        onChange={val => setTempActionType(Array.isArray(val) ? val[0] : val)}
                                        placeholder="Select action type">
                                        {actionOptions.map(opt => (
                                            <ComboboxItem key={opt.id} caption={opt.title} value={opt.id}/>
                                        ))}
                                    </ComboBox>
                                </div>
                            </HeaderFilter>
                        </Cell>
                        <Cell width={600}>Action Description</Cell>
                        <Cell width={250}>Initiator</Cell>
                        <Cell width={300}>Timestamp</Cell>
                        <Cell>Reason</Cell>
                    </Row>
                </Head>
                <Body>
                    {entries.map((entry, index) => (
                        <Row key={entry.recordId ?? `${entry.timestamp}-${index}`}>
                            <Cell>{(page - 1) * pageSize + index + 1}</Cell>
                            <Cell>{getTitleById(entry.actionName) ?? entry.actionName}</Cell>
                            <Cell><DescriptionCell description={entry.actionDescription} query={searchQuery}/></Cell>
                            <Cell>
                                <EllipsisTextHint title={formatInitiator(entry)}>
                                    {formatInitiator(entry)}
                                </EllipsisTextHint>
                            </Cell>
                            <Cell><TimestampCell timestamp={entry.timestamp}/></Cell>
                            <Cell>
                                <EllipsisTextHint title={entry.reason ?? '—'}>
                                    {entry.reason ?? '—'}
                                </EllipsisTextHint>
                            </Cell>
                        </Row>
                    ))}
                </Body>
            </Table>
            <div className={auditLogPagination}>
                <PaginationStripe
                    goToPage={true}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    currentPage={page}
                    onPaginationChange={handlePaginationChange}
                    onChangePaginationSize={handlePageSizeChange}
                    sizeSwitcherPosition="LEFT"
                    sizeSwitcherType="ONLY_PAGE_SIZE"
                    sizes={[MIN_PAGE_SIZE, 50]}
                />
            </div>
        </>
    );
};
